// Notes.js - Backend-synced notes for manufacturing + lot_tracking + stock_material
import React, { useState, useEffect } from 'react';
import { RiDeleteBin6Fill } from 'react-icons/ri';
import Swal from 'sweetalert2';

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

const getAuthToken = () => {
  const possibleKeys = ['access_token', 'token', 'accessToken', 'authToken', 'jwt'];
  for (let key of possibleKeys) {
    const val = localStorage.getItem(key);
    if (val && val !== 'null' && val !== 'undefined') {
      try {
        const parsed = JSON.parse(val);
        if (parsed && typeof parsed === 'object') {
          const inner = parsed.access || parsed.token || parsed.access_token;
          if (inner) return inner;
        }
      } catch (e) { return val; }
      return val;
    }
  }
  return null;
};

const buildHeaders = (withJson = true) => {
  const h = {};
  if (withJson) h['Content-Type'] = 'application/json';
  const token = getAuthToken();
  if (token && token !== 'null' && token !== 'undefined') {
    h['Authorization'] = `Bearer ${token}`;
  }
  return h;
};

const Notes = ({ trackingId, mode = 'manufacturing' }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const supplierId = window.__SUPPLIER_ID__ || null;

  // ✅ URL builders — includes stock_material mode
  const getListUrl = () => {
    if (mode === 'lot_tracking' && supplierId) {
      return `${API_BASE_URL}/inventory/suppliers/${supplierId}/lot-tracking/${trackingId}/notes/all/`;
    }
    if (mode === 'stock_material') {
      return `${API_BASE_URL}/inventory/stock-material/${trackingId}/notes/all/`;
    }
    return `${API_BASE_URL}/inventory/api/manufacturing/${trackingId}/notes/all/`;
  };

  const getAddUrl = () => {
    if (mode === 'lot_tracking' && supplierId) {
      return `${API_BASE_URL}/inventory/suppliers/${supplierId}/lot-tracking/${trackingId}/notes/`;
    }
    if (mode === 'stock_material') {
      return `${API_BASE_URL}/inventory/stock-material/${trackingId}/notes/`;
    }
    return `${API_BASE_URL}/inventory/api/manufacturing/${trackingId}/notes/`;
  };

  const getDeleteUrl = (noteId) => {
    if (mode === 'lot_tracking' && supplierId) {
      return `${API_BASE_URL}/inventory/suppliers/${supplierId}/lot-tracking/${trackingId}/notes/${noteId}/delete/`;
    }
    if (mode === 'stock_material') {
      return `${API_BASE_URL}/inventory/stock-material/${trackingId}/notes/${noteId}/delete/`;
    }
    return `${API_BASE_URL}/inventory/api/manufacturing/${trackingId}/notes/${noteId}/delete/`;
  };

  useEffect(() => {
    const fetchNotes = async () => {
      if (!trackingId || trackingId === 'new') return;
      try {
        setLoading(true);
        const url = getListUrl();
        console.log('📥 Fetching notes from:', url);
        const response = await fetch(url, { headers: buildHeaders(false) });
        if (!response.ok) {
          console.warn('⚠️ Fetch notes failed:', response.status);
          setNotes([]);
          return;
        }
        const data = await response.json();
        const rawList = Array.isArray(data) ? data : (data.notes || []);
        const mapped = rawList.map(n => ({
          id: n.id,
          content: n.content,
          created_by: n.created_by || 'Admin',
          created_at: n.created_at,
        }));
        console.log(`✅ Loaded ${mapped.length} notes`);
        setNotes(mapped);
      } catch (err) {
        console.error('❌ Error fetching notes:', err);
        setNotes([]);
      } finally {
        setLoading(false);
      }
    };
    fetchNotes();
  }, [trackingId, mode]);

  const addNote = async () => {
    if (!newNote.trim()) {
      Swal.fire({ icon: 'warning', title: 'Please enter a note', confirmButtonColor: '#004aad' });
      return;
    }
    if (!trackingId || trackingId === 'new') {
      Swal.fire({
        icon: 'warning', title: 'Save Main Form First',
        text: 'Please save the record first before adding notes.',
        confirmButtonColor: '#004aad',
      });
      return;
    }

    try {
      setSaving(true);
      const url = getAddUrl();
      const body = { content: newNote.trim() };
      console.log('📤 Adding note to:', url, body);

      const response = await fetch(url, {
        method: 'POST',
        headers: buildHeaders(true),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errMsg = `HTTP ${response.status}`;
        try { const t = await response.text(); errMsg += ` - ${t}`; } catch {}
        throw new Error(errMsg);
      }

      const saved = await response.json();
      setNotes(prev => [...prev, {
        id: saved.id,
        content: saved.content || newNote.trim(),
        created_by: saved.created_by || 'Admin',
        created_at: saved.created_at || new Date().toISOString(),
      }]);
      setNewNote('');
      Swal.fire({
        icon: 'success', title: 'Note Added Successfully',
        confirmButtonColor: '#004aad', timer: 1500, showConfirmButton: false,
      });
    } catch (err) {
      console.error('❌ Add note error:', err);
      Swal.fire({ icon: 'error', title: 'Failed to Add Note', text: err.message, confirmButtonColor: '#dc2626' });
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    const result = await Swal.fire({
      icon: 'warning', title: 'Delete Note?', text: 'This note will be permanently deleted.',
      showCancelButton: true, confirmButtonColor: '#dc2626', cancelButtonColor: '#666',
      confirmButtonText: 'Yes, delete it!', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      const url = getDeleteUrl(noteId);
      const response = await fetch(url, { method: 'DELETE', headers: buildHeaders(false) });
      if (!response.ok && response.status !== 204) throw new Error(`Failed to delete: ${response.status}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      Swal.fire({ icon: 'success', title: 'Note Deleted', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
    } catch (err) {
      console.error('❌ Delete note error:', err);
      Swal.fire({ icon: 'error', title: 'Delete Failed', text: err.message, confirmButtonColor: '#dc2626' });
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    try {
      const d = new Date(isoString);
      return d.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      });
    } catch { return isoString; }
  };

  return (
    <div className="notes-content">
      <div className="notes-container">
        <h3 className="notes-title">Production Notes</h3>
        <div className="notes-display">
          {loading ? (
            <p className="no-notes-message">Loading notes...</p>
          ) : notes.length === 0 ? (
            <p className="no-notes-message">No notes available</p>
          ) : (
            <div className="notes-list">
              {notes.map(note => (
                <div key={note.id} className="note-item">
                  <div className="note-content">{note.content}</div>
                  <div className="note-meta">
                    <span className="note-created-by">{note.created_by}</span>
                    <span className="note-created-at">{formatDate(note.created_at)}</span>
                    <button className="note-delete-btn" onClick={() => deleteNote(note.id)} title="Delete Note" type="button">
                      <RiDeleteBin6Fill />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="notes-input-section">
          <textarea className="notes-textarea-new" placeholder="Add a new note..."
            value={newNote} onChange={(e) => setNewNote(e.target.value)}
            rows={4} disabled={saving} />
          <button type="button" className="add-note-btn" onClick={addNote}
            disabled={saving || !trackingId || trackingId === 'new'}>
            {saving ? 'Saving...' : 'Add Note'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Notes;